import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useSubscriptionStore } from '@/features/subscription/stores/subscription.store'
import { useUserStore } from '@/features/user/stores/user.store'

export function useSubscription() {
  const subscriptionStore = useSubscriptionStore()
  const userStore = useUserStore()

  const {
    activating, purchasing, cancelling, updatingPayment, invoicesLoading, loadingPlanDetails,
    requestingSales, planInfoLoading, savingCompany, invitableUsersLoading,
    invoices, countries, invitableUsers, planChangeDetails, planInfo, error,
  } = storeToRefs(subscriptionStore)

  // SearchPlanIds.ORGANIC_FREE — the free trial plan (V2 special-cases planId == 5).
  const ORGANIC_FREE_PLAN_ID = 5

  const { subscription, profile } = storeToRefs(userStore)

  const planName = computed(() => profile.value?.PlanName ?? '')
  const currentPlanId = computed(() => profile.value?.PlanId ?? null)
  const isOrganic = computed(() => subscription.value?.IsOrganic ?? false)
  const subscriptionIsPaid = computed(() => subscription.value?.SubscriptionIsPaid ?? false)
  const paymentFailed = computed(() => subscription.value?.PaymentFailed ?? false)
  const allowUpgrade = computed(() => subscription.value?.AllowUpgrade ?? false)
  const currencyCode = computed(() => subscription.value?.CurrencyCode ?? 'GBP')

  // V2 parity: an organic account that hasn't activated/paid reads as "On Trial"
  // (Vue 2 has no separate "Pending Payment" label — the trial state IS the label).
  const statusLabel = computed<'Active' | 'Payment Failed' | 'On Trial' | 'Unknown'>(() => {
    if (!subscription.value) return 'Unknown'
    if (paymentFailed.value) return 'Payment Failed'
    if (subscriptionIsPaid.value) return 'Active'
    return 'On Trial'
  })

  const statusColor = computed(() => {
    switch (statusLabel.value) {
      case 'Active': return 'success'
      case 'Payment Failed': return 'error'
      case 'On Trial': return 'warning'
      default: return 'grey'
    }
  })

  const shouldShowAlertBanner = computed(() => {
    if (!subscription.value) return false
    if (paymentFailed.value) return true
    if (isOrganic.value && !subscriptionIsPaid.value && allowUpgrade.value) return true
    return false
  })

  // V2 parity: the self-serve area (Actions + Change plan) is admin-only AND
  // organic-self-upgradeable-only. V2 gates it on `isOrganic && allowUpgrade &&
  // isAppAdmin`; since the backend defines allowUpgrade => isOrganic, that reduces
  // to `allowUpgrade && isAdmin`. Non-organic (contract/Enterprise) accounts see
  // only the plan summary — they manage billing through sales, not self-serve.
  const isAdmin = computed(() => userStore.isAdmin)
  const canManageSubscription = computed(() => isAdmin.value && allowUpgrade.value)

  // Billing cadence label for the credit rows (parity with Vue 2's creditReleaseType).
  const creditReleaseType = computed(() =>
    planInfo.value?.planIsAnnual ? 'per year' : 'per month',
  )

  const exportCreditsDisplay = computed(() =>
    planInfo.value ? `${planInfo.value.exportCredits.toLocaleString('en-US')} / ${creditReleaseType.value}` : '—',
  )
  const mobileCreditsDisplay = computed(() =>
    planInfo.value ? `${planInfo.value.mobileCredits.toLocaleString('en-US')} / ${creditReleaseType.value}` : '—',
  )
  const priceDisplay = computed(() => planInfo.value?.planPrice || '—')
  const billingDisplay = computed(() => planInfo.value?.planBillingType || '—')

  // ── Trial banner (V2 parity) ──────────────────────────────────────────────
  const isFreePlan = computed(() => currentPlanId.value === ORGANIC_FREE_PLAN_ID)
  // Shown for organic accounts that haven't activated/paid yet and can upgrade.
  const showTrialBanner = computed(
    () => isOrganic.value && !subscriptionIsPaid.value && allowUpgrade.value,
  )
  const unrestrictedExportDisplay = computed(() =>
    (planInfo.value?.unrestrictedExportCredits ?? 0).toLocaleString('en-US'),
  )
  const unrestrictedMobileDisplay = computed(() =>
    (planInfo.value?.unrestrictedMobileCredits ?? 0).toLocaleString('en-US'),
  )
  const trialBannerText = computed(() =>
    isFreePlan.value
      ? 'Please upgrade to a paid plan below to unlock additional features and credits.'
      : `To unlock the full features and credits of your selected plan, please activate your subscription below. Once activated, you will have access to ${unrestrictedExportDisplay.value} export credits and ${unrestrictedMobileDisplay.value} mobile credits ${creditReleaseType.value}.`,
  )

  // ── Company Details (V2 parity) ───────────────────────────────────────────
  const showCompanyDetails = computed(() => isOrganic.value)
  // V2: `isOrganic && isAppAdmin && (!subscriptionIsPaid || planId == 5)`.
  const canEditCompanyDetails = computed(
    () =>
      isOrganic.value &&
      isAdmin.value &&
      (!subscriptionIsPaid.value || isFreePlan.value),
  )
  const companyName = computed(() => planInfo.value?.companyName ?? '')
  const companyWebsite = computed(() => planInfo.value?.companyWebsite ?? '')
  const companyCountryId = computed(() => planInfo.value?.companyCountryId ?? 0)
  const companyCountryName = computed(() => planInfo.value?.companyCountryName ?? '')

  async function ensureSubscriptionLoaded(): Promise<void> {
    await Promise.all([
      subscription.value ? Promise.resolve() : userStore.fetchSubscription(),
      planInfo.value ? Promise.resolve() : subscriptionStore.loadPlanInfo(),
    ])
  }

  return {
    activating, purchasing, cancelling, updatingPayment, invoicesLoading, loadingPlanDetails,
    requestingSales, planInfoLoading, invoices, planChangeDetails, planInfo, error,
    subscription, planName, currentPlanId,
    isOrganic, subscriptionIsPaid, paymentFailed, allowUpgrade, currencyCode,
    statusLabel, statusColor, shouldShowAlertBanner,
    isAdmin, canManageSubscription,
    exportCreditsDisplay, mobileCreditsDisplay, priceDisplay, billingDisplay,
    isFreePlan, showTrialBanner, trialBannerText,
    showCompanyDetails, canEditCompanyDetails,
    companyName, companyWebsite, companyCountryId, companyCountryName,
    countries, savingCompany,
    invitableUsers, invitableUsersLoading,
    loadCountries: subscriptionStore.loadCountries,
    saveCompanyDetails: subscriptionStore.saveCompanyDetails,
    loadInvitableUsers: subscriptionStore.loadInvitableUsers,
    ensureSubscriptionLoaded,
    activatePlan: subscriptionStore.activatePlan,
    purchasePlan: subscriptionStore.purchasePlan,
    loadPlanChangeDetails: subscriptionStore.loadPlanChangeDetails,
    clearPlanChangeDetails: subscriptionStore.clearPlanChangeDetails,
    requestSalesContact: subscriptionStore.requestSalesContact,
    updatePaymentMethod: subscriptionStore.updatePaymentMethod,
    cancelSubscription: subscriptionStore.cancelSubscription,
    loadInvoices: subscriptionStore.loadInvoices,
    $reset: subscriptionStore.$reset,
  }
}
